import { EventEmitter, Injectable } from "@angular/core";
import { SyncableStatus } from "../../../core/database/db";
import { AreaDTOSyncable } from "../../../domains/areas/models/AreaDTO";
import { AssertionDTOSyncable } from "../../../domains/assertions/models/AssertionDTO";
import { ComponentDTOSyncable } from "../../../domains/components/models/ComponentDTO";
import { JobIssueDTOSyncable } from "../../../domains/jobIssues/models/IssueDTO";
import { JobAssetDtoSyncable } from "../../../domains/media/models/JobAssetDto";
import { JobIssueVendorDTOSyncable } from "../../../domains/vendors/models/JobIssueVendorDTO";
import { JobSignatureDTOSyncable } from "../../../domains/jobs/models/JobSignatureDTO";
import { ZoneDTOSyncable } from "../../../domains/zones/models/ZoneDTO";
import { CONSTANTS } from "../../../constants";
import DataSyncService, { BqaUpdateData } from "./data-sync.service";
import { LoggerService } from "../../../shared/services/logger.service";
import { hash } from "../../../shared/utils/hashing-helper";
import SyncStatusService, { SyncStatus } from "../sync-status-service";
import { DataSyncMetadataApiService } from "./data-sync-metadata-api.service";

export interface SyncMetadata {
  localTotalCount: number;
  localPendingCount: number;
  localHash: string;
  remoteCount: number;
  remoteHash: string;
}

export type EffectiveSyncMetadata = Omit<SyncMetadata, "localHash" | "remoteHash" | "localPendingCount">;

export interface BqaJobSyncMetadata {
  bqStep: number;
  jobAddress: string;
  isJobComplete: boolean;
  areas: SyncMetadata;
  assertions: SyncMetadata;
  childIssues: SyncMetadata;
  components: SyncMetadata;
  images: SyncMetadata;
  issues: SyncMetadata;
  issueVendors: SyncMetadata;
  job: SyncMetadata;
  media: SyncMetadata;
  signatures: SyncMetadata;
  zones: SyncMetadata;
}

interface BqaMetadataForLogging {
  jobId: string;
  bqStep: number;
  jobAddress: string;
}

export interface BqaMetadataForLoggingWithMetadata extends BqaMetadataForLogging {
  metadataForJob: BqaJobSyncMetadata;
}

@Injectable({
  providedIn: "root"
})
export class DataSyncMetadataService {
  constructor(
    private dataSyncMetadataApiService: DataSyncMetadataApiService,
    private dataSyncService: DataSyncService,
    private logger: LoggerService,
    private syncStatusService: SyncStatusService
  ) { }

  syncMetadataChanged: EventEmitter<Record<string, BqaJobSyncMetadata>> = new EventEmitter();

  async bootstrapSyncMetadata() {
    const metadata = this.getSyncMetadata();
    const metadataExists = metadata !== null && Object.keys(metadata).length > 0;
    if (metadataExists === false) {
      this.logger.info("[SyncMetadata] No existing sync metadata found. Bootstrapping new metadata.");
      await this.updateSyncMetadata();
    }
  }

  async updateSyncMetadata(isImageSync = false, areUploadsStillInProgress = false): Promise<void> {
    try {
      const isOnline = navigator.onLine;
      const shouldUpdateRemoteCounts = isImageSync === false && isOnline;
      const metadata = await this.updateSyncMetadataAfterSync(shouldUpdateRemoteCounts);

      if (isOnline) {
        const submittedJobsNotFullySycned = this.getSubmittedJobsNotFullySynced(metadata);
        const areAnySubmittedJobsNotFullySynced = submittedJobsNotFullySycned.length > 0;
        if (areUploadsStillInProgress) {
          this.logger.info("[SyncMetadata] Uploads are still in progress after sync. Delay checking for not fully sycned jobs.");
        } else if (areAnySubmittedJobsNotFullySynced) {
          this.logger.info("[SyncMetadata] Some jobs are still not fully synced after sync. Setting sync status to Incomplete.");
          this.logger.event("SyncMetadata:JobsNotFullySyncedAfterSync", {
            submittedJobsNotFullySycned
          });
          this.syncStatusService.setSyncStatus(SyncStatus.Incomplete);
        } else {
          this.syncStatusService.setSyncStatus(SyncStatus.Complete);
        }
      } else {
        this.logger.info("[SyncMetadata] Device is offline after sync. Not checking for incomplete jobs as remote counts cannot be updated.");
      }

      this.syncMetadataChanged.emit(metadata);
    } catch (error) {
      this.logger.error(`[SyncMetadata] Failed to update sync metadata after ${isImageSync ? "image upload completion" : "sync"}.`, error);
    }
  }

  getSyncMetadata() {
    const metadataString = localStorage.getItem("bqaSyncMetadata");
    return metadataString ? JSON.parse(metadataString) as Record<string, BqaJobSyncMetadata> : null;
  }

  getEffectiveSyncMetadata(syncMetadata: SyncMetadata): EffectiveSyncMetadata {
    const { localTotalCount, localPendingCount, remoteCount } = syncMetadata;
    let effectiveRemoteCount = remoteCount;
    const effectiveLocalTotalCount = localTotalCount;

    const shouldCheckPendingCount = localTotalCount === remoteCount;
    if (shouldCheckPendingCount) {
      effectiveRemoteCount = remoteCount - localPendingCount;
    }

    return {
      localTotalCount: effectiveLocalTotalCount,
      remoteCount: effectiveRemoteCount,
    };
  }

  public getSubmittedJobsFullySynced(metadata: Record<string, BqaJobSyncMetadata>) {
    const jobIds = Object.keys(metadata);
    const completeJobsFullySynced: BqaMetadataForLoggingWithMetadata[] = [];

    for (const jobId of jobIds) {
      const metadataForJob = metadata[jobId];

      if (metadataForJob.isJobComplete === false) {
        continue;
      }

      const metadataEntries = Object
        .entries(metadataForJob)
        .filter(([_key, value]: [string, SyncMetadata | number | string]) => typeof value === "object") as [string, SyncMetadata][];

      let isJobFullySynced = true;

      for (const entry of metadataEntries) {
        const [, value] = entry;
        const { localTotalCount, remoteCount } = this.getEffectiveSyncMetadata(value);
        if (remoteCount < localTotalCount) {
          isJobFullySynced = false;
          break;
        }
      }

      if (isJobFullySynced) {
        completeJobsFullySynced.push({ jobId, bqStep: metadataForJob.bqStep, jobAddress: metadataForJob.jobAddress, metadataForJob });
      }
    }
    return completeJobsFullySynced;
  }

  private getSubmittedJobsNotFullySynced(metadata: Record<string, BqaJobSyncMetadata>) {
    const jobIds = Object.keys(metadata);
    const completeJobsNotFullySynced: BqaMetadataForLoggingWithMetadata[] = [];
    const incompleteJobs: BqaMetadataForLogging[] = [];

    for (const jobId of jobIds) {
      const metadataForJob = metadata[jobId];

      if (metadataForJob.isJobComplete === false) {
        incompleteJobs.push({ jobId, bqStep: metadataForJob.bqStep, jobAddress: metadataForJob.jobAddress });
        continue;
      }

      const metadataEntries = Object
        .entries(metadataForJob)
        .filter(([_key, value]: [string, SyncMetadata | number | string]) => typeof value === "object") as [string, SyncMetadata][];

      for (const entry of metadataEntries) {
        const [key, value] = entry;
        const { localTotalCount, remoteCount } = this.getEffectiveSyncMetadata(value);
        if (remoteCount < localTotalCount) {
          this.logger.warn(`[SyncMetadata] Job ID ${jobId}, address ${metadataForJob.jobAddress} is incomplete for sync category '${key}': ${remoteCount}/${localTotalCount} items synced.`);
          completeJobsNotFullySynced.push({ jobId, bqStep: metadataForJob.bqStep, jobAddress: metadataForJob.jobAddress, metadataForJob });
        }
      }
    }

    if (incompleteJobs.length > 0) {
      this.logger.info(`
        [SyncMetadata] the following job(s) are marked as incomplete based on job's isComplete property.

        ${incompleteJobs.map(j => `Job ID: ${j.jobId}, BQ Step: ${j.bqStep}, Address: ${j.jobAddress}`).join("\n")}

        This jobs will not be checked for incomplete sync status as it's still actively being worked.`
      );
    }

    return completeJobsNotFullySynced;
  }

  private async updateSyncMetadataAfterSync(shouldUpdateRemoteCounts: boolean) {
    // NOTE: deliberately not wrapped in an explicit db.transaction() - see the equivalent note in
    // DataSyncService.getDataFromDatabaseToSync for why a long-lived transaction here can leak its
    // zone into unrelated concurrent Dexie calls elsewhere in the app.
    this.syncStatusService.addSyncProgressItem("GatherSyncMetaData");
    this.logger.timeStart("SyncMetadata.GetDataFromDatabaseToSync");
    const allData = await this.dataSyncService.getDataFromDatabaseToSync(true);
    this.logger.timeEnd("SyncMetadata.GetDataFromDatabaseToSync");
    const calculatedMetadata = await this.calculateSyncMetadata(allData);
    this.syncStatusService.markSyncProgressItemComplete("GatherSyncMetaData");

    let updatedMetadata;
    if (shouldUpdateRemoteCounts) {
      this.syncStatusService.addSyncProgressItem("UpdateSyncMetaData");
      this.logger.timeStart("SyncMetadata.UpdateMetaDataApi");
      updatedMetadata = await this.dataSyncMetadataApiService.updateMetaData(calculatedMetadata);
      this.logger.timeEnd("SyncMetadata.UpdateMetaDataApi");
      this.syncStatusService.markSyncProgressItemComplete("UpdateSyncMetaData");
    } else {
      updatedMetadata = calculatedMetadata;
    }

    this.logger.info("[SyncMetadata] Updating sync metadata", { metadata: updatedMetadata });

    localStorage.setItem("bqaSyncMetadata", JSON.stringify(updatedMetadata));

    return updatedMetadata;
  }

  private async calculateSyncMetadata(updateData: BqaUpdateData): Promise<Record<string, BqaJobSyncMetadata>> {
    const metadata: Record<string, BqaJobSyncMetadata> = {};
    const existingMetadataString = this.getSyncMetadata() ?? {};

    for (const job of updateData.jobs) {
      const zoneData = new Map<string, ZoneDTOSyncable>();
      const signatureData = new Map<string, JobSignatureDTOSyncable>();
      const issueData = new Map<string, JobIssueDTOSyncable>();
      const childIssueData = new Map<string, JobIssueDTOSyncable>();
      const componentData = new Map<string, ComponentDTOSyncable>();
      const assertionData = new Map<string, AssertionDTOSyncable>();
      const mediaData = new Map<string, JobAssetDtoSyncable>();
      const areaData = new Map<string, AreaDTOSyncable>();
      const uploadedImageData = new Map<string, JobAssetDtoSyncable>();
      const issueVendors = new Map<string, JobIssueVendorDTOSyncable>();
      const bqStep = job.buildQualityStep;
      const jobAddress = job.streetAddress ?? "unknown address";
      const isJobComplete = job.isComplete;

      this.calculateSyncMetadataForZones(
        bqStep,
        job.id,
        job.zones,
        zoneData,
        areaData,
        componentData,
        assertionData,
        mediaData,
        uploadedImageData,
        childIssueData,
        issueData,
        issueVendors
      );

      this.calculateSyncMetadataForZones(
        bqStep,
        job.id,
        job.lot?.zones || [],
        zoneData,
        areaData,
        componentData,
        assertionData,
        mediaData,
        uploadedImageData,
        childIssueData,
        issueData,
        issueVendors
      );

      this.calculateSyncMetadataForJobIssues(
        job.issues || [],
        mediaData,
        uploadedImageData,
        childIssueData,
        issueData,
        issueVendors
      );

      job.signatures.forEach(signature => signatureData.set(signature.identifier, signature));

      this.calculateSyncMetadataForJobIssues(
        updateData.jobIssues?.filter(issue => issue.jobId === job.id) || [],
        mediaData,
        uploadedImageData,
        childIssueData,
        issueData,
        issueVendors
      );

      const existingMetadataForJob = existingMetadataString[job.id];

      metadata[job.id] = {
        bqStep,
        jobAddress,
        isJobComplete,
        // Job is a special case and can't use `formatMetadata` function since it doesn't have a collection of items, so we calculate its metadata properties directly here
        job: {
          localTotalCount: 1,
          localHash: await hash(job.id.toString()),
          remoteCount: job.syncStatus === SyncableStatus.Completed ? 1 : 0,
          remoteHash: await hash(job.syncStatus === SyncableStatus.Completed ? job.id.toString() : ""),
          localPendingCount: job.syncStatus === SyncableStatus.Pending ? 1 : 0,
        },
        areas: await this.formatMetadata(areaData, existingMetadataForJob?.areas),
        assertions: await this.formatMetadata(assertionData, existingMetadataForJob?.assertions),
        childIssues: await this.formatMetadata(childIssueData, existingMetadataForJob?.childIssues),
        components: await this.formatMetadata(componentData, existingMetadataForJob?.components),
        // Images is a special case since they are uploaded to blob storage directly from the browser
        images: {
          localTotalCount: mediaData.size,
          localHash: await hash([...mediaData].sort().join("")),
          remoteCount: uploadedImageData.size,
          remoteHash: await hash([...uploadedImageData].sort().join("")),
          localPendingCount: 0
        },
        issues: await this.formatMetadata(issueData, existingMetadataForJob?.issues),
        issueVendors: await this.formatMetadata(issueVendors, existingMetadataForJob?.issueVendors),
        media: await this.formatMetadata(mediaData, existingMetadataForJob?.media),
        signatures: await this.formatMetadata(signatureData, existingMetadataForJob?.signatures),
        zones: await this.formatMetadata(zoneData, existingMetadataForJob?.zones)
      };
    }

    return metadata;
  }

  private async formatMetadata(metadataCollection: Map<string, { syncStatus: SyncableStatus }>, existingMetadata: SyncMetadata): Promise<SyncMetadata> {
    const localTotalCount = metadataCollection.size;
    const localHash = await hash([...metadataCollection.keys()].sort().join(""));
    const remoteCount = existingMetadata?.remoteCount || 0;
    const remoteHash = existingMetadata?.remoteHash || "";
    const localPendingCount = [...metadataCollection.values()].filter(x => x.syncStatus === SyncableStatus.Pending).length;

    return {
      localTotalCount,
      localHash,
      remoteCount,
      remoteHash,
      localPendingCount
    };
  }

  private calculateSyncMetadataForZones(bqStep: number, jobId: number, zones: ZoneDTOSyncable[], zoneData: Map<string, ZoneDTOSyncable>, areaData: Map<string, AreaDTOSyncable>, componentData: Map<string, ComponentDTOSyncable>, assertionData: Map<string, AssertionDTOSyncable>, mediaData: Map<string, JobAssetDtoSyncable>, uploadedImageData: Map<string, JobAssetDtoSyncable>, childIssueData: Map<string, JobIssueDTOSyncable>, issueData: Map<string, JobIssueDTOSyncable>, issueVendors: Map<string, JobIssueVendorDTOSyncable>) {
    zones.forEach(zone => {

      zoneData.set(zone.id.toString(), zone);
      zone.areas.forEach(area => {

        areaData.set(area.identifier, area);

        // For BQ8, we only want to calculate metadata for zones and areas, so we skip components, assertions, media, and issues
        const shouldSkipComponentsAndAssertions = bqStep === CONSTANTS.BUILD_QUALITY_STEPS.BQ8;
        if (shouldSkipComponentsAndAssertions) {
          return;
        }

        area.components.forEach(component => {
          componentData.set(component.identifier, component);
          component.assertions.forEach(assertion => {

            assertionData.set(assertion.identifier, assertion);
            assertion.media?.forEach(media => {
              mediaData.set(media.mediaGuid, media);
              if (media.isUploaded) {
                uploadedImageData.set(media.mediaGuid, media);
              }
            });

            this.calculateSyncMetadataForJobIssues(
              // Assertions are shared across BQ steps on a lot, so an assertion carries issues from every
              // job (e.g. BQ6 and BQ7). Count only THIS job's issues so per-job counts match the server
              // (which keys by jobId) instead of double-counting a sibling job's issues and media.
              (assertion.issues || []).filter(issue => issue.jobId === jobId),
              mediaData,
              uploadedImageData,
              childIssueData,
              issueData,
              issueVendors
            );
          });
        });
      });
    });
  }

  private calculateSyncMetadataForJobIssues(jobIssues: JobIssueDTOSyncable[], mediaData: Map<string, JobAssetDtoSyncable>, uploadedImageData: Map<string, JobAssetDtoSyncable>, childIssueData: Map<string, JobIssueDTOSyncable>, issueData: Map<string, JobIssueDTOSyncable>, issueVendors: Map<string, JobIssueVendorDTOSyncable>) {
    jobIssues.forEach(issue => {
      if (issue.parentIdentifier) {
        childIssueData.set(issue.identifier, issue);
      } else {
        issueData.set(issue.identifier, issue);
      }

      issue.media?.forEach(media => {
        mediaData.set(media.mediaGuid, media);
        if (media.isUploaded) {
          uploadedImageData.set(media.mediaGuid, media);
        }
      });
      issue.jobIssueVendors?.forEach(vendor => {
        issueVendors.set(vendor.issueVendorIdentifier, vendor);
      });
    });
  }
}